export interface AccountUser {
  id: string;
  email: string;
  name: string;
  image: string | null;
}

export interface AccountSession {
  user: AccountUser;
  expiresAt: string;
}

export type AccountProvider = "github" | "google";

export interface AccountProviderStatus {
  id: AccountProvider;
  configured: boolean;
}

const ACCOUNT_AUTH_ORIGIN = "https://accounts.codepawl.com/api/auth";

interface ApiError {
  code?: string;
  message?: string;
}

async function responseJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null) as (T & { error?: ApiError }) | null;
  if (!response.ok || !body) throw new Error(body?.error?.message || "Roam account service is unavailable.");
  return body;
}

export async function getAccountSession(signal?: AbortSignal): Promise<AccountSession | null> {
  const response = await fetch("/api/auth/session", { credentials: "include", signal });
  const body = await responseJson<{ ok: true; session: AccountSession | null }>(response);
  return body.session;
}

export async function beginAccountAuth(action: "login" | "signup", turnstileToken: string): Promise<string> {
  const response = await fetch(`/api/auth/${action}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", "x-turnstile-response": turnstileToken },
    body: JSON.stringify({ returnTo: "/" }),
  });
  const body = await responseJson<{ ok: true; authorizationUrl: string }>(response);
  return body.authorizationUrl;
}

export async function getAccountProviders(signal?: AbortSignal): Promise<AccountProviderStatus[]> {
  const response = await fetch(`${ACCOUNT_AUTH_ORIGIN}/providers`, { credentials: "include", signal });
  const body = await responseJson<{ providers: AccountProviderStatus[] }>(response);
  return body.providers.filter((provider): provider is AccountProviderStatus =>
    (provider.id === "github" || provider.id === "google") && typeof provider.configured === "boolean",
  );
}

export async function beginProviderAuth(
  action: "login" | "signup",
  provider: AccountProvider,
  turnstileToken: string,
): Promise<string> {
  const authorizationUrl = await beginAccountAuth(action, turnstileToken);
  const response = await fetch(`${ACCOUNT_AUTH_ORIGIN}/sign-in/social`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      provider,
      callbackURL: authorizationUrl,
      requestSignUp: action === "signup",
    }),
  });
  const body = await response.json().catch(() => null) as { url?: string; message?: string; error?: { message?: string } } | null;
  if (!response.ok || !body?.url) {
    throw new Error(body?.error?.message || body?.message || `${provider === "github" ? "GitHub" : "Google"} sign-in could not start.`);
  }
  return body.url;
}

export async function signOutAccount(): Promise<void> {
  const response = await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  await responseJson<{ ok: true }>(response);
}
export interface CloudStateResponse {
  state: unknown | null;
  revision: number;
  updatedAt: string | null;
}

export async function getCloudState(signal?: AbortSignal): Promise<CloudStateResponse> {
  const response = await fetch("/api/state", { credentials: "include", signal });
  return responseJson<{ ok: true } & CloudStateResponse>(response);
}

export async function putCloudState(state: unknown, expectedRevision: number): Promise<{ revision: number; updatedAt: string }> {
  const response = await fetch("/api/state", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ state, expectedRevision }),
  });
  if (response.status === 409) {
    const body = await response.json() as { error?: { details?: CloudStateResponse; message?: string } };
    throw Object.assign(new Error(body.error?.message || "Cloud data changed on another device."), { code: "SYNC_CONFLICT", latest: body.error?.details });
  }
  return responseJson<{ ok: true; revision: number; updatedAt: string }>(response);
}

export async function deleteCloudState(): Promise<void> {
  const response = await fetch("/api/state", {
    method: "DELETE",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ confirmation: "DELETE" }),
  });
  await responseJson<{ ok: true }>(response);
}
